import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'
import FsOFASlider from './fs-ofa-slider'
import { FsOFASliderR } from '../../shared/model/models'
import { getFormattedString } from '../../shared/utils'
import { Module } from '../../shared/model'

const FsOFASliderRow = ({
  rows,
  ofaVariable,
  isItemSelected,
  id,
  onChange,
  moduleConfig,
  sliderChanges,
}: FsOFASliderR) => {
  const cwiRows = rows
    .filter((ro) => ro.ofaVariable === ofaVariable)
    .sort((a, b) => Number(a.newGap) - Number(b.newGap))
  const [curIndex, setCurIndex] = useState(
    cwiRows.findIndex((crw) =>
      sliderChanges[ofaVariable]
        ? crw.id === (sliderChanges[ofaVariable].changedId ?? sliderChanges[ofaVariable].curId)
        : crw.isCurrentOption,
    ),
  )
  const cur = cwiRows[curIndex]
  const { t, i18n } = useTranslation()

  useEffect(() => {
    if (!isItemSelected) setCurIndex(cwiRows.findIndex((crw) => crw.isCurrentOption))
  }, [isItemSelected])

  return (
    <>
      <TableRow
        key={`row2_${id}`}
        sx={{
          height: isItemSelected ? 12 : 0,
          display: isItemSelected ? 'table-row' : 'none',
        }}
      >
        <TableCell
          colSpan={moduleConfig?.module === Module.CAREER ? 3 : 4}
          sx={{
            borderLeft: `1px solid ${moduleConfig?.fsColor.primary}`,
            borderBottom: 0,
            pr: 0,
          }}
        >
          <Box sx={{ borderBottom: `2px solid ${moduleConfig?.fsColor.light}` }} px={1} py={0.5}>
            <Typography fontWeight="bold">{t('ofa.edit.action')}</Typography>
          </Box>
        </TableCell>
        {cur && (
          <>
            <TableCell
              sx={{
                borderBottom: 0,
                px: 0,
              }}
            >
              <Box
                sx={{ borderBottom: `2px solid ${moduleConfig?.fsColor.light}` }}
                py={0.5}
                px={2}
              >
                <Typography fontWeight="bold">{`${getFormattedString(
                  i18n.language,
                  cur?.newGap ?? '',
                  1,
                  1,
                )} %`}</Typography>
              </Box>
            </TableCell>
            <TableCell
              sx={{
                borderBottom: 0,
                px: 0,
              }}
            >
              <Box
                sx={{ borderBottom: `2px solid ${moduleConfig?.fsColor.light}` }}
                py={0.5}
                px={2}
              >
                <Typography fontWeight="bold">
                  {moduleConfig?.module === Module.CAREER
                    ? getFormattedString(i18n.language, cur?.additionalCmAverage ?? '', 0, 0, true)
                    : `${getFormattedString(
                        i18n.language,
                        cur?.additionalCmAverage ?? '',
                        2,
                        2,
                        true,
                      )} ${t(`${moduleConfig?.unit}` as any)}`}
                </Typography>
              </Box>
            </TableCell>
            <TableCell
              sx={{
                borderBottom: 0,
                px: 0,
              }}
            >
              <Box
                sx={{ borderBottom: `2px solid ${moduleConfig?.fsColor.light}` }}
                py={0.5}
                px={2}
              >
                <Typography fontWeight="bold">
                  {`${getFormattedString(
                    i18n.language,
                    cur?.additionalCost ?? '',
                    2,
                    2,
                    true,
                  )} \u20ac`}
                </Typography>
              </Box>
            </TableCell>
            <TableCell
              sx={{
                borderRight: `1px solid ${moduleConfig?.fsColor.primary}`,
                borderBottom: 0,
                pl: 0,
              }}
            >
              <Box
                sx={{ borderBottom: `2px solid ${moduleConfig?.fsColor.light}` }}
                py={0.5}
                px={1}
              >
                <Typography fontWeight="bold">{`${getFormattedString(
                  i18n.language,
                  cur?.costAsPercentageOfPayroll ?? '',
                  0,
                  3,
                )} %`}</Typography>
              </Box>
            </TableCell>
          </>
        )}
      </TableRow>
      <TableRow
        key={`row3_${id}`}
        sx={{
          height: isItemSelected ? 20 : 0,
          display: isItemSelected ? 'table-row' : 'none',
        }}
      >
        {cur && (
          <TableCell
            colSpan={8}
            sx={{
              border: `1px solid ${moduleConfig?.fsColor.primary}`,
              borderTop: 0,
              verticalAlign: 'baseline',
            }}
          >
            <Stack direction="row" pt={3} pb={2} width="100%" px={6}>
              <Stack mr={4} mt={-0.75} textAlign="center">
                <Typography>
                  {t(Number(cwiRows[0].newGap) === 0 ? 'ofa.recommended' : 'ofa.current')}
                </Typography>
                <Typography>{`${Number(cwiRows[0].newGap).toLocaleString(i18n.language, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 1,
                })} %`}</Typography>
              </Stack>
              <FsOFASlider
                value={curIndex}
                onChange={(val: number) => {
                  setCurIndex(val)
                  onChange(ofaVariable, cwiRows[val].id)
                }}
                min={0}
                max={cwiRows.length - 1}
                step={null}
                marks={cwiRows.map((c) => ({ id: c.id, gap: c.newGap }))}
                formatter={(value: number) =>
                  `${Number(value).toLocaleString(i18n.language, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 1,
                  })} %`
                }
                moduleConfig={moduleConfig}
              />
              <Stack ml={4} mt={-0.75} textAlign="center">
                <Typography>
                  {t(
                    Number(cwiRows[cwiRows.length - 1].newGap) === 0
                      ? 'ofa.recommended'
                      : 'ofa.current',
                  )}
                </Typography>
                <Typography>{`${Number(cwiRows[cwiRows.length - 1].newGap).toLocaleString(
                  i18n.language,
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 1,
                  },
                )} %`}</Typography>
              </Stack>
            </Stack>
          </TableCell>
        )}
      </TableRow>
    </>
  )
}

export default FsOFASliderRow
